import apiClient from '@/axios/app-client';
import { Department, Staff } from '@/features/companies/types';
import { FunctionName } from '@/lib/enums';

export interface PropertyName {
  id: string;
  name: string;
}

export interface GetPropertiesNamesParams {
  functionName: FunctionName;
  hasArchived?: boolean;
}

const PROPERTIES_ENDPOINT = '/v1/properties';

// Field Staff for API Response
export interface PropertyFieldStaffResponse {
  id?: string;
  displayOrder: number;
  name: string;
  phoneNumber: string;
  mailAddress: string | null;
}

// Contract Factory for API Response
export interface PropertyContractFactoryResponse {
  id?: string;
  displayOrder: number;
  factoryName: string;
  deliveryCategory: string;
  quantity: number | null;
}

// Receipt Document for API Response
export interface PropertyReceiptDocumentResponse {
  id: string;
  classification: number;
  title: string;
  remarks: string;
}

export interface PropertyCustomerSupplier {
  id?: string | number;
  name?: string;
  abbreviationName?: string;
  formalName?: string;
}

export interface PropertyConstructionCompany {
  id?: string | number;
  name?: string;
  nameAbbreviation?: string;
}

// Property API Response Type (camelCase) - Based on actual API response
export interface PropertyApiResponse {
  id: string; // UUID
  waoPropertyCode: string;
  name: string; // Not propertyName
  nameAbbreviation: string; // Not propertyNameAbbreviation
  postalCodeFirst: number;
  postalCodeLast: number;
  address: string;
  startDate: string;
  ownerName: string;
  publicPrivateClassificationCode: number | null;
  customerId: string;
  constructionCompanyId: string;
  constructionPeriodFrom: string | null;
  constructionPeriodTo: string | null;
  officeAddress: string;
  officeTel: string;
  officeFax: string;
  structuralFormatCode: number | null;
  structuralFormatOthers: string | null;
  numberOfFloorsAboveGround: string;
  numberOfBasementFloors: string;
  siteManagerName: string;
  siteManagerTel: string;
  rmcContractQuantity: number | null;
  vehicleSize: number | null;
  departmentCode: number;
  salesRepresentativeCode: number;
  deliveryPersonCode: number;
  achievementConfirmationCompany: string;
  noharaDirect: string;
  pWindow: string;
  sellersOtherGc: string;
  checkMethod: number | null;
  memo: string;
  resultsCaution: string;
  jvPrice: number | null;
  generalContractorPurchasePrice: number | null;
  existsPurchaseOrder: boolean | null;
  cooperative: string;
  isCommonCement?: boolean | null; // Optional - may not be in response
  linkCementCode?: number;
  commercialDistributionMap: string;
  managerComment: string;
  version: number;
  isArchive: boolean;
  createdAt: string;
  createdModule: string;
  createdUser: string;
  updatedAt: string;
  updatedModule: string;
  updatedUser: string;
  isDeleted: boolean;
  fieldStaffs: PropertyFieldStaffResponse[];
  contractFactories: PropertyContractFactoryResponse[];
  receiptDocuments: PropertyReceiptDocumentResponse[];
  isCanUpdate?: boolean;
  isCanManagePrice?: boolean;
  saleStaff: Staff;
  deliveryStaff: Staff;
  department: Department;
  createdStaff: Staff;
  updatedStaff: Staff;
  plans: { id: string }[];
  customerSupplier: PropertyCustomerSupplier;
  constructionCompany: PropertyConstructionCompany;
}

// Base interfaces for nested items (shared between Create and Update)
// Field Staff base interface
export interface BasePropertyFieldStaff {
  id?: string; // Optional - UUID for existing items (update), omit for new items (create)
  displayOrder: number;
  name: string;
  phoneNumber: string;
  mailAddress: string;
}

// Contract Factory base interface
export interface BasePropertyContractFactory {
  id?: string; // Optional - UUID for existing items (update), omit for new items (create)
  displayOrder: number;
  factoryName: string;
  deliveryCategory: string;
  quantity: number | null;
}

// Receipt Document base interface
export interface BasePropertyReceiptDocument {
  id?: string; // Optional - UUID for existing items (update), omit for new items (create)
  classification: number;
  title: string;
  remarks: string;
}

// Type aliases for better semantic clarity
export type CreatePropertyFieldStaff = BasePropertyFieldStaff;
export type CreatePropertyContractFactory = BasePropertyContractFactory;
export type CreatePropertyReceiptDocument = BasePropertyReceiptDocument;

export type UpdatePropertyFieldStaff = BasePropertyFieldStaff;
export type UpdatePropertyContractFactory = BasePropertyContractFactory;
export type UpdatePropertyReceiptDocument = BasePropertyReceiptDocument;

// Base Property Request interface (shared fields between Create and Update)
export interface BasePropertyRequest {
  waoPropertyCode?: string;
  name: string;
  nameAbbreviation: string;
  postalCodeFirst?: number;
  postalCodeLast?: number;
  address: string;
  startDate: string;
  ownerName?: string;
  publicPrivateClassificationCode?: number;
  customerId: string;
  constructionCompanyId: string;
  constructionPeriodFrom?: string;
  constructionPeriodTo?: string;
  officeAddress?: string;
  officeTel?: string;
  officeFax?: string;
  structuralFormatCode?: number;
  structuralFormatOthers?: string;
  numberOfFloorsAboveGround?: string;
  numberOfBasementFloors?: string;
  siteManagerName?: string;
  siteManagerTel?: string;
  rmcContractQuantity?: number;
  vehicleSize?: number;
  departmentCode: number;
  salesRepresentativeCode: number | null;
  deliveryPersonCode: number | null;
  achievementConfirmationCompany?: string;
  noharaDirect?: string;
  pWindow?: string;
  sellersOtherGc?: string;
  checkMethod?: number;
  memo?: string;
  resultsCaution?: string;
  jvPrice?: number;
  generalContractorPurchasePrice?: number;
  existsPurchaseOrder?: boolean;
  cooperative?: string;
  isCommonCement?: boolean;
  linkCementCode?: number;
  commercialDistributionMap?: string;
  managerComment?: string;
}

// Create Property Request (no receiptDocuments required)
export interface CreatePropertyRequest extends BasePropertyRequest {
  fieldStaffs: CreatePropertyFieldStaff[];
  contractFactories: CreatePropertyContractFactory[];
}

// Update Property Request (includes receiptDocuments array)
export interface UpdatePropertyRequest extends BasePropertyRequest {
  fieldStaffs: UpdatePropertyFieldStaff[];
  contractFactories: UpdatePropertyContractFactory[];
  receiptDocuments: UpdatePropertyReceiptDocument[];
}

export const propertiesService = {
  /**
   * Get property by ID
   */
  async getPropertyById(id: string) {
    return apiClient.get<PropertyApiResponse>(`${PROPERTIES_ENDPOINT}/${id}`);
  },

  /**
   * Create a new property
   */
  async createProperty(propertyData: CreatePropertyRequest) {
    return apiClient.post<PropertyApiResponse>(`${PROPERTIES_ENDPOINT}`, propertyData);
  },

  /**
   * Update a property
   */
  async updateProperty(id: string, propertyData: UpdatePropertyRequest) {
    return apiClient.put<PropertyApiResponse>(`${PROPERTIES_ENDPOINT}/${id}`, propertyData);
  },

  /**
   * Get property names
   */
  async getPropertyNames(params: GetPropertiesNamesParams): Promise<PropertyName[]> {
    const response = await apiClient.get<PropertyName[]>(`${PROPERTIES_ENDPOINT}/names`, params);
    // API returns array directly from apiFn.handler
    return response.data.data;
  },

  /**
   * Delete property by ID
   */
  async deleteProperty(id: string) {
    return apiClient.delete(`${PROPERTIES_ENDPOINT}/${id}`);
  },

  /**
   * Archive property by ID
   */
  async archiveProperty(ids: string[]) {
    return apiClient.put(`${PROPERTIES_ENDPOINT}/archive`, { propertyIds: ids, isArchive: true });
  },

  /**
   * Restore property by ID
   */
  async restoreProperty(ids: string[]) {
    return apiClient.put(`${PROPERTIES_ENDPOINT}/archive`, { propertyIds: ids, isArchive: false });
  },
};
